/**
 * TravelRadiusMap — small Leaflet map centered on a US ZIP with a live
 * expanding circle for the "max travel distance" slider on the player profile.
 *
 * Props:
 *   zip           5-digit US ZIP string
 *   radiusMiles   number of miles OR null/undefined = draw a country-wide view
 *   height        CSS height (default "260px")
 *
 * Geocoding uses the free Zippopotam service. Result is cached in-memory
 * for the lifetime of the tab so tweaking the slider doesn't refetch.
 */
import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { MapPin } from "lucide-react";
import { geocodeZip } from "@/lib/geocodeZip";
import { clipCircleToLand, preloadLand } from "@/lib/landClip";
import { US_STATES } from "@/lib/usStates";

// Full state name -> 2-letter abbreviation, for labelling each state on the map.
const STATE_ABBREV = Object.fromEntries(US_STATES.map(([abbr, name]) => [name, abbr]));

// Major US metros, ordered biggest-first. On the small map we place as many
// labels as fit without overlapping (bigger cities win the space).
const MAJOR_CITIES = [
    ["New York", 40.7128, -74.006],
    ["Los Angeles", 34.0522, -118.2437],
    ["Chicago", 41.8781, -87.6298],
    ["Dallas", 32.7767, -96.797],
    ["Houston", 29.7604, -95.3698],
    ["Washington, D.C.", 38.9072, -77.0369],
    ["Philadelphia", 39.9526, -75.1652],
    ["Atlanta", 33.749, -84.388],
    ["Miami", 25.7617, -80.1918],
    ["Phoenix", 33.4484, -112.074],
    ["Boston", 42.3601, -71.0589],
    ["San Francisco", 37.7749, -122.4194],
    ["Detroit", 42.3314, -83.0458],
    ["Seattle", 47.6062, -122.3321],
    ["Minneapolis", 44.9778, -93.265],
    ["San Diego", 32.7157, -117.1611],
    ["Tampa", 27.9506, -82.4572],
    ["Denver", 39.7392, -104.9903],
    ["St. Louis", 38.627, -90.1994],
    ["Baltimore", 39.2904, -76.6122],
    ["Charlotte", 35.2271, -80.8431],
    ["Orlando", 28.5383, -81.3792],
    ["San Antonio", 29.4241, -98.4936],
    ["Portland", 45.5152, -122.6784],
    ["Sacramento", 38.5816, -121.4944],
    ["Pittsburgh", 40.4406, -79.9959],
    ["Las Vegas", 36.1699, -115.1398],
    ["Cincinnati", 39.1031, -84.512],
    ["Kansas City", 39.0997, -94.5786],
    ["Columbus", 39.9612, -82.9988],
    ["Indianapolis", 39.7684, -86.1581],
    ["Cleveland", 41.4993, -81.6944],
    ["Nashville", 36.1627, -86.7816],
    ["Austin", 30.2672, -97.7431],
    ["Raleigh", 35.7796, -78.6382],
    ["Milwaukee", 43.0389, -87.9065],
    ["Jacksonville", 30.3322, -81.6557],
    ["Memphis", 35.1495, -90.049],
    ["Oklahoma City", 35.4676, -97.5164],
    ["Louisville", 38.2527, -85.7585],
    ["New Orleans", 29.9511, -90.0715],
    ["Salt Lake City", 40.7608, -111.891],
    ["Richmond", 37.5407, -77.436],
    ["Buffalo", 42.8864, -78.8784],
    ["Birmingham", 33.5186, -86.8104],
    ["Albuquerque", 35.0844, -106.6504],
    ["Boise", 43.615, -116.2023],
    ["Omaha", 41.2565, -95.9345],
    ["Hartford", 41.7658, -72.6734],
    ["Providence", 41.824, -71.4128],
    ["Rochester", 43.1566, -77.6088],
    ["Grand Rapids", 42.9634, -85.6681],
    ["Tucson", 32.2226, -110.9747],
    ["Fresno", 36.7378, -119.7871],
    ["Tulsa", 36.154, -95.9928],
    ["Honolulu", 21.3069, -157.8583],
    ["Albany", 42.6526, -73.7562],
    ["Knoxville", 35.9606, -83.9207],
    ["El Paso", 31.7619, -106.485],
    ["Des Moines", 41.5868, -93.625],
    ["Little Rock", 34.7465, -92.2896],
    ["Wichita", 37.6872, -97.3301],
    ["Colorado Springs", 38.8339, -104.8214],
    ["Baton Rouge", 30.4515, -91.1871],
    ["Columbia", 34.0007, -81.0348],
    ["Greenville", 34.8526, -82.394],
    ["Charleston", 32.7765, -79.9311],
    ["Chattanooga", 35.0456, -85.3097],
    ["Spokane", 47.6588, -117.426],
    ["Reno", 39.5296, -119.8138],
    ["Toledo", 41.6528, -83.5379],
    ["Madison", 43.0731, -89.4012],
    ["Lexington", 38.0406, -84.5037],
    ["Norfolk", 36.8508, -76.2859],
    ["Greensboro", 36.0726, -79.792],
    ["Syracuse", 43.0481, -76.1474],
    ["Sioux Falls", 43.546, -96.7313],
    ["Fargo", 46.8772, -96.7898],
    ["Billings", 45.7833, -108.5007],
    ["Anchorage", 61.2181, -149.9003],
    ["Portland, ME", 43.6591, -70.2568],
    ["Burlington", 44.4759, -73.2121],
    ["Manchester", 42.9956, -71.4548],
    ["Wilmington", 39.7391, -75.5398],
    ["Virginia Beach", 36.8529, -75.978],
    ["Jackson", 32.2988, -90.1848],
    ["Mobile", 30.6954, -88.0399],
    ["Savannah", 32.0809, -81.0912],
    ["Montgomery", 32.3668, -86.3],
    ["Cheyenne", 41.14, -104.8202],
    ["Missoula", 46.8721, -113.994],
    ["Bismarck", 46.8083, -100.7837],
    ["Rapid City", 44.0805, -103.231],
    ["Lincoln", 40.8136, -96.7026],
    ["Springfield", 37.209, -93.2923],
    ["Shreveport", 32.5252, -93.7502],
    ["Fayetteville", 36.0626, -94.1574],
    ["Provo", 40.2338, -111.6585],
    ["Idaho Falls", 43.4917, -112.0339],
    ["Cedar Rapids", 41.9779, -91.6656],
    ["Dayton", 39.7589, -84.1916],
    ["Bakersfield", 35.3733, -119.0187],
    ["San Jose", 37.3382, -121.8863],
];

let statesPromise = null;
function loadStates() {
    if (!statesPromise) {
        statesPromise = fetch("/us-states.geojson").then((r) => (r.ok ? r.json() : null)).catch(() => null);
    }
    return statesPromise;
}

// Approximate zoom level that comfortably fits a `radiusMiles` circle.
function zoomForRadius(radiusMiles) {
    if (!radiusMiles) return 4;              // Unlimited: continental view
    if (radiusMiles >= 800) return 3;
    if (radiusMiles >= 500) return 4;
    if (radiusMiles >= 300) return 5;
    if (radiusMiles >= 150) return 6;
    if (radiusMiles >= 75) return 7;
    return 8;
}

export default function TravelRadiusMap({ zip, radiusMiles, height = "260px", color = "#ff4500" }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const circleRef = useRef(null);
    const markerRef = useRef(null);
    const labelsRef = useRef(null);        // layer group for state + city labels (re-laid out on view change)
    const stateCentroidsRef = useRef([]);  // [{ abbr, lat, lng }]
    const [point, setPoint] = useState(null);
    const [status, setStatus] = useState("idle");

    // Place as many labels as fit without overlapping. State abbreviations get
    // priority, then cities biggest-first — losers keep their dot but drop the label.
    const relayoutLabels = () => {
        const map = mapRef.current;
        if (!map) return;
        if (!labelsRef.current) labelsRef.current = L.layerGroup().addTo(map);
        const layer = labelsRef.current;
        layer.clearLayers();
        const size = map.getSize();
        const occupied = [];
        const pad = 2;
        const hits = (b) => occupied.some((o) => !(b[2] < o[0] - pad || b[0] > o[2] + pad || b[3] < o[1] - pad || b[1] > o[3] + pad));
        const inView = (p) => p.x >= 0 && p.x <= size.x && p.y >= 0 && p.y <= size.y;

        stateCentroidsRef.current.forEach(({ abbr, lat, lng }) => {
            const p = map.latLngToContainerPoint([lat, lng]);
            if (!inView(p)) return;
            const box = [p.x - 9, p.y - 6, p.x + 9, p.y + 6];
            if (hits(box)) return;
            occupied.push(box);
            L.marker([lat, lng], {
                interactive: false,
                icon: L.divIcon({
                    className: "",
                    html: `<span style="font:600 10px/1 ui-sans-serif,system-ui;letter-spacing:.08em;color:#c7c7d2;text-shadow:0 1px 2px #000,0 0 2px #000">${abbr}</span>`,
                    iconSize: [22, 12],
                    iconAnchor: [11, 6],
                }),
            }).addTo(layer);
        });

        MAJOR_CITIES.forEach(([name, lat, lng]) => {
            const p = map.latLngToContainerPoint([lat, lng]);
            if (!inView(p)) return;
            L.circleMarker([lat, lng], {
                interactive: false, radius: 2.5, color: "#f5f5f7", fillColor: "#f5f5f7", fillOpacity: 1, weight: 0,
            }).addTo(layer);
            const w = name.length * 5.2 + 4;
            const box = [p.x + 3, p.y - 6, p.x + 3 + w, p.y + 6];
            if (hits(box)) return;
            occupied.push(box);
            L.marker([lat, lng], {
                interactive: false,
                icon: L.divIcon({
                    className: "",
                    html: `<span style="font:500 9px/1 ui-sans-serif,system-ui;color:#e8e8ee;text-shadow:0 1px 2px #000,0 0 2px #000;white-space:nowrap">${name}</span>`,
                    iconSize: [w, 10],
                    iconAnchor: [-4, 5],
                }),
            }).addTo(layer);
        });
    };

    // Init the map once.
    useEffect(() => {
        preloadLand();
        if (!containerRef.current || mapRef.current) return;
        mapRef.current = L.map(containerRef.current, {
            zoomControl: false,
            attributionControl: false,
            dragging: false,
            scrollWheelZoom: false,
            doubleClickZoom: false,
            touchZoom: false,
            boxZoom: false,
            keyboard: false,
        }).setView([39.8283, -98.5795], 4);
        L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}", {
            maxZoom: 16,
        }).addTo(mapRef.current);

        // Redraw labels whenever the view settles (radius/zip changes call setView).
        mapRef.current.on("moveend zoomend", relayoutLabels);

        // Thick state boundaries (static) + compute label centroids, then lay out labels.
        loadStates().then((geo) => {
            const map = mapRef.current;
            if (!map || !geo) return;
            L.geoJSON(geo, {
                interactive: false,
                style: { color: "#7c7c8a", weight: 1.6, opacity: 0.85, fill: false },
            }).addTo(map);
            stateCentroidsRef.current = geo.features
                .map((f) => {
                    const abbr = STATE_ABBREV[f.properties?.name];
                    if (!abbr) return null;
                    const c = L.geoJSON(f).getBounds().getCenter();
                    return { abbr, lat: c.lat, lng: c.lng };
                })
                .filter(Boolean);
            relayoutLabels();
        });
        return () => {
            mapRef.current?.remove();
            mapRef.current = null;
        };
    }, []);

    // Geocode the ZIP whenever it changes to a valid 5-digit number.
    useEffect(() => {
        let cancelled = false;
        setStatus(zip ? "loading" : "idle");
        geocodeZip(zip).then((p) => {
            if (cancelled) return;
            setPoint(p);
            setStatus(p ? "ok" : "invalid");
        });
        return () => { cancelled = true; };
    }, [zip]);

    // Redraw pin + circle when the point or radius changes.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !point) return;
        map.setView([point.lat, point.lng], zoomForRadius(radiusMiles));

        if (!markerRef.current) {
            markerRef.current = L.circleMarker([point.lat, point.lng], {
                radius: 6,
                color: color,
                fillColor: color,
                fillOpacity: 1,
                weight: 0,
            }).addTo(map);
        } else {
            markerRef.current.setLatLng([point.lat, point.lng]);
        }

        // Remove circle when radius is null (Unlimited).
        if (!radiusMiles) {
            if (circleRef.current) {
                map.removeLayer(circleRef.current);
                circleRef.current = null;
            }
            return;
        }

        // Draw a water-clipped radius shape (never spills into the ocean).
        let cancelled = false;
        clipCircleToLand(point.lat, point.lng, radiusMiles).then((geom) => {
            if (cancelled || !mapRef.current) return;
            if (circleRef.current) {
                map.removeLayer(circleRef.current);
                circleRef.current = null;
            }
            circleRef.current = L.geoJSON(geom, {
                smoothFactor: 0,
                style: {
                    color: color,
                    weight: 2,
                    fillColor: color,
                    fillOpacity: 0.12,
                },
                interactive: false,
            }).addTo(map);
        });
        return () => { cancelled = true; };
    }, [point, radiusMiles, color]);

    return (
        <div className="border border-[#2a2a2a] bg-[#141414] relative overflow-hidden" data-testid="travel-radius-map">
            <div ref={containerRef} style={{ height, background: "#0a0a0a" }} />

            {/* Empty-ZIP overlay — a centered prompt that dims the map until a ZIP is entered. */}
            {(status === "idle" || status === "invalid") && (
                <div
                    data-testid="travel-radius-empty-hint"
                    className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm text-center px-6 pointer-events-none"
                >
                    <MapPin className="h-6 w-6 mb-3" strokeWidth={2.5} style={{ color }} />
                    <div className="text-sm text-white font-medium">
                        {status === "invalid" ? "That ZIP doesn't look right" : "Add a home ZIP to preview your travel radius"}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mt-1.5">
                        {status === "invalid" ? "Enter a valid 5-digit US ZIP above" : "Enter a 5-digit US ZIP above"}
                    </div>
                </div>
            )}

            {/* Small status pill (only when a valid ZIP is loaded). */}
            {status === "ok" && point && (
                <div className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] bg-black/60 px-2 py-1 pointer-events-none">
                    <span className="text-white">{point.label}</span>
                    <span className="mx-2 text-[#2a2a2a]">|</span>
                    <span style={{ color }}>
                        {!radiusMiles ? "Unlimited range" : `${radiusMiles} mi radius`}
                    </span>
                </div>
            )}
            {status === "loading" && (
                <div className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] bg-black/60 px-2 py-1 pointer-events-none">
                    Locating…
                </div>
            )}
        </div>
    );
}
